import styles from "../../../styles/SubscribeInformations.module.scss";
import { useMemo } from "react";
import { getStripeProductFromDiseases, applyDiscount } from "../../../utils/liberals";
import SummaryCard from "./SummaryCard";
import Line from "./Line";
import {
  STRIPE_PRODUCTS_LS_ID,
  DISEASE_LS_ID,
  STRIPE_INFO,
  BFC_SUFFIX,
} from "../constants";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { LoadableText } from "../../UI/LoadableText";
import InformationsForm from "./InformationsForm";
import axios from "axios";
import { pluralize } from "../../../utils/utils";

/**
 * Root component of the `/subscribe/liberal` route, @ step 3 "Informations"
 *
 * @param {Function} nextStep - The function to call when the user wants to go to the next step
 * @param {Function} previousStep - The function to call when the user wants to go to the previous step
 */
export default function SubscribeInformations({ nextStep = () => {}, lastStep = () => {} }) {
  const [localStorageProducts] = useLocalStorage(STRIPE_PRODUCTS_LS_ID, []);
  const [localStorageDiseases] = useLocalStorage(DISEASE_LS_ID, []);
  const [stripeInfo, setStripeInfo] = useLocalStorage(STRIPE_INFO, null);

  const selectedProduct = useMemo(
    () => applyDiscount(getStripeProductFromDiseases(localStorageProducts, localStorageDiseases), stripeInfo?.discount),
    [localStorageProducts, localStorageDiseases, stripeInfo?.discount]
  )

  function getRandomHex(length) {
    let result = "";
    const hexChars = "0123456789abcdef";
    for (let i = 0; i < length; i++) {
      result += hexChars[Math.floor(Math.random() * 16)];
    }
    return result;
  }

  function generateUsername(firstname, lastname) {
    const uniqueId = getRandomHex(8);
    return `${firstname.toLowerCase()}.${lastname.toLowerCase()}${uniqueId}`.replaceAll(" ", "");
  }

  function generatePassword(length = 12) {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    return password;
  }

  function handleCreateSubscription(userInfo, isBFC = false) {
    if (isBFC) {
      axios.get(process.env.REACT_APP_NUVEE_BASE_URL + `/users-permissions/roles`)
      .then((res) => {
        if (!res?.data?.roles) return;

        const roleId = res.data.roles.find((role) => role.name === "pro_standard")?.id || 0;
        if (!roleId) return;

        // Calculate the userThematics array based on the selected diseases and their thematics
        const thematics = localStorageDiseases.flatMap(disease => disease.thematics);
        const userThematics = thematics.reduce((acc, thematic) => {
          const thematicTypes = thematic.contents.map(c => c.thematicType);
          for (const thematicType of thematicTypes) {
            const existing = acc.findIndex(ut => ut.thematicType === thematicType.id);
            if (existing === -1) {
              acc.push({ thematicType: thematicType.id, thematics: [thematic.id] });
            } else {
              acc[existing].thematics.push(thematic.id);
            }
          }
          return acc;
        }, []);

        const newPass = generatePassword();
        const proData = {
          username:
            generateUsername(userInfo.firstname, userInfo.lastname) + BFC_SUFFIX,
          password: newPass,
          // I need to send firstpassword to trigger lifecycle and send inscription email.
          firstpassword: newPass,
          role: roleId,
          email: userInfo.email,
          firstname: userInfo.firstname,
          lastname: userInfo.lastname,
          userThematics,
          status: "INI",
          provider: "local",
          Contact: {
            phone: userInfo.phone,
            address: userInfo.address,
            city: userInfo.city,
            postalCode: userInfo.postalCode,
          },
          Additional_Information: [
            {
              __component: "additionalinformation.professional",
              professional_type: userInfo.job,
              speciality: userInfo.job,
              structure: userInfo.structure,
              number_of_accounts: 100,
              eETP: true,
            },
          ],
        };

        const url = process.env.REACT_APP_NUVEE_BASE_URL + `/users`;
        return axios.post(url, proData)
      })
      .then((res) => {
        if (res?.data?.id) {
          lastStep();
        }
      })
      .catch((error) => {
        console.error(error);
      });
    } else {
      const customerInfo = {
        email: userInfo.email,
        name: `${userInfo.firstname}, ${userInfo.lastname}`,
        metadata: {
          ...userInfo,
          thematics: JSON.stringify(
            localStorageDiseases.map((disease) => disease.id)
          )
        }
      };

      const url = process.env.REACT_APP_NUVEE_BASE_URL + `/stripe-subscriptions/create`;
      axios.post(url, {
        productId: selectedProduct.id,
        customerInfo,
        discountCode: stripeInfo?.discount?.discount_code,
      })
      .then((response) => {
        setStripeInfo({
          ...stripeInfo,
          clientSecret: response.data.clientSecret,
          customer: response.data.customer
        })
        nextStep();
      })
      .catch((error) => {
        console.error(error);
        alert("Une erreur est survenue lors de l'enregistrement de vos informations, veuillez réessayer plus tard. Si le problème persiste, merci de vous rapprocher de notre support.")
      });
    }
  }

  return (
    <div className={styles.layoutContainer}>
      <div className={styles.item}>
        <InformationsForm onSubmit={handleCreateSubscription} />
      </div>
      <SummaryCard title="Récapitulatif de votre commande" styles={styles}>
        <div className={styles.cardPart}>
          <Line subtitle="Offre :" styles={styles}>
            <div>{selectedProduct.name}</div>
          </Line>
          <Line subtitle={pluralize("Thématique", localStorageDiseases.length)+ " :"} styles={styles}>
            <ul className={styles.list}>
              {localStorageDiseases.map(
                (disease) =>
                  disease && (
                    <li key={disease.id} style={{ color: "#145A79" }}>
                      <LoadableText text={disease.name} />
                    </li>
                  )
              )}
            </ul>
          </Line>
          {stripeInfo?.discount && (
            <Line subtitle="Code de réduction" styles={styles}>
              <div className={styles.discount}>
                {stripeInfo.discount.discount_code}: - {stripeInfo.discount.amount_off ?? stripeInfo.discount.percent_off}
                {stripeInfo.discount.amount_off ? "€" : "%"}
              </div>
            </Line>
          )}
        </div>

        <div className={styles.cardPart}>
          <Line subtitle="Montant" details="HT" styles={styles}>
            <div className={styles.price}>
              <LoadableText text={selectedProduct.localized_price_subtaxes} />
            </div>
          </Line>

          <Line subtitle="TVA" details="20%" styles={styles}>
            <div className={styles.price}>
              <LoadableText text={selectedProduct.localized_price_taxes} />
            </div>
          </Line>

          <Line subtitle="Total" details="T.T.C" styles={styles}>
            <div className={styles.price}>
              <LoadableText text={selectedProduct.localized_price} />
            </div>
          </Line>

          <div className={styles.subtitle}>Nous acceptons :</div>
          <div className={styles.imgContainer}>
            <img src="/images/liberals/payment/cb.svg" alt="CB" />
            <img src="/images/liberals/payment/sepa.png" alt="SEPA" />
          </div>
        </div>
      </SummaryCard>
    </div>
  );
}
